import { useEffect, useState, type CSSProperties } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { logout, whoami } from "../../lib/auth";
import type { SessionUser } from "../../types/auth";
import AdminNav from "./AdminNav";
import AdminBottomNav from "./AdminBottomNav";
import IncomingCallBanner, { type IncomingAlert } from "../common/IncomingCallBanner";
import TableSetupAlertBanner from "../common/TableSetupAlertBanner";
import { playSpeakeasyChime } from "../../lib/audio";
import { supabase } from "../../lib/supabase";
import ErrorBoundary from "../common/ErrorBoundary";

const DESKTOP_BREAKPOINT = 1024;

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState<boolean>(() =>
    typeof window === "undefined" ? false : window.innerWidth < DESKTOP_BREAKPOINT
  );
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < DESKTOP_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [globalAlert, setGlobalAlert] = useState<IncomingAlert | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { pathname } = useLocation();

  // Close the mobile menu sheet once a tool is picked. Without this the sheet stayed open over
  // the page that was just navigated to.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    let mounted = true;
    void whoami().then((u) => {
      if (!mounted) return;
      setUser(u);
      setLoaded(true);
      if (!u) {
        navigate("/login", { replace: true });
        return;
      }
      const isOwner = u.bar_roles.includes("bar_owner");
      const isManager = u.bar_roles.includes("bar_manager");
      const isManagerOrOwner = isManager || isOwner;
      const isStaff = u.bar_roles.some(
        (r) => r === "bar_staff" || r === "bar_bartender" || r === "bar_lead"
      );

      // Paths restricted strictly to owners
      const ownerOnlyPaths = [
        "/admin/recipes",
        "/admin/prep-recipes",
        "/admin/ingredients",
        "/admin/cocktail-mapping",
        "/admin/tasting-mat",
        "/admin/flights",
        "/admin/staff-access",
        "/admin/station-tablets",
      ];

      // Floor & Service operational paths that staff can access on mobile
      const staffAllowedPaths = [
        "/admin/cash-management",
        "/admin/operations",
        "/admin/pick-list",
        "/admin/shelf-map",
        "/admin/events",
      ];

      const currentPath = window.location.pathname;

      if (!isOwner && ownerOnlyPaths.some((p) => currentPath.startsWith(p))) {
        navigate("/", { replace: true });
        return;
      }

      if (!isManagerOrOwner && (!isStaff || !staffAllowedPaths.some((p) => currentPath.startsWith(p)))) {
        navigate("/", { replace: true });
      }
    });
    return () => {
      mounted = false;
    };
  }, [navigate]);

  // Global background listener for incoming softphone calls and SMS alerts (Managers & Owners only)
  useEffect(() => {
    if (!user) return;

    const isManagerOrOwner = user.bar_roles.some(
      (r) => r === "bar_manager" || r === "bar_owner"
    );
    if (!isManagerOrOwner) return;

    const channel = supabase
      .channel("global_comms_monitor")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "comms_threads",
        },
        (payload: any) => {
          if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
            const row = payload.new;
            // Suppress banner if user is already looking at the communications inbox
            if (window.location.pathname === "/admin/communications") return;

            if (row && (payload.eventType === "INSERT" || (row.unread_count && Number(row.unread_count) > 0))) {
              const isUrgent = row.priority === "urgent";
              playSpeakeasyChime(isUrgent ? "urgent" : "message");
              setGlobalAlert({
                id: row.id,
                type: isUrgent ? "urgent" : "message",
                title: row.customer_name || row.identity_norm || "Guest Message",
                subtitle: row.last_message || "Inbound communication received at the speakeasy",
                onAccept: () => {
                  navigate("/admin/communications");
                },
              });
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, navigate]);

  function onLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  if (!loaded) {
    return (
      <main style={styles.loading}>
        <p style={{ color: "var(--muted)" }}>Loading admin…</p>
      </main>
    );
  }
  if (!user) return null;
  const isOwner = user.bar_roles.includes("bar_owner");
  const isManager = user.bar_roles.includes("bar_manager");
  const isHost = user.bar_roles.includes("bar_host");
  const isManagerOrOwner = isManager || isOwner;
  const isStaff = user.bar_roles.some((r) => r === "bar_staff" || r === "bar_bartender" || r === "bar_lead");
  if (!isManagerOrOwner && !isStaff) {
    return null;
  }

  return (
    <div style={isMobile ? styles.mobileShell : styles.desktopShell}>
      {/* Global incoming call and SMS alert overlay */}
      <IncomingCallBanner alert={globalAlert} onDismiss={() => setGlobalAlert(null)} />
      {/* Host stand party arrival & table setup alert banner */}
      <TableSetupAlertBanner />
      {/* WCAG 2.4.1 — Bypass Blocks. Visually hidden until focused. */}
      <a href="#admin-content" className="skip-link sr-only">
        Skip to content
      </a>
      {!isMobile && <AdminNav barRoles={user.bar_roles} isMobile={false} />}
      <div style={styles.contentColumn}>
        <header style={isMobile ? styles.mobileHeader : styles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Link to="/" title="Return to Sentinel OS Hub" style={{ display: "flex", alignItems: "center", textDecoration: "none", gap: 6 }}>
              <img
                src="/img/sentinel-logo.svg"
                alt="The Sentinel Room"
                style={{ height: isMobile ? 22 : 26, width: "auto", display: "block" }}
              />
              <span
                style={{
                  fontSize: isMobile ? 11 : 12,
                  fontWeight: 900,
                  color: "var(--gold, #F9AF36)",
                  letterSpacing: "0.08em",
                  padding: "1px 5px",
                  background: "rgba(249, 175, 54, 0.14)",
                  border: "1px solid rgba(249, 175, 54, 0.35)",
                  borderRadius: 4,
                  lineHeight: 1.2,
                }}
              >
                OS
              </span>
            </Link>
          </div>
          <div style={styles.headerRight}>
            {!isMobile && isManagerOrOwner && (
              <>
                <Link to="/host" style={styles.linkBtn}>
                  Host Stand
                </Link>
                <Link to="/station" style={styles.linkBtn}>
                  Station Mode
                </Link>
              </>
            )}
            {!isMobile && isHost && !isManagerOrOwner && (
              <Link to="/host" style={styles.linkBtn}>
                Host Stand
              </Link>
            )}
            <Link to="/" style={isMobile ? styles.mobileLinkBtn : styles.linkBtn}>
              ← {isMobile ? "Hub" : "OS Hub"}
            </Link>
          </div>
        </header>
        
        {/* Full Mobile Nav Sheet Drawer */}
        {isMobile && mobileMenuOpen && (
          <div className="mobile-sheet-backdrop admin-menu-sheet" onClick={() => setMobileMenuOpen(false)}>
            <div
              className="mobile-sheet-content"
              onClick={(e) => {
                e.stopPropagation();
                // Tapping the page you're already on doesn't change the path; close anyway.
                if ((e.target as HTMLElement).closest("a")) setMobileMenuOpen(false);
              }}
            >
              <div className="mobile-sheet-handle" />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: "var(--gold)", letterSpacing: 0.5, textTransform: "uppercase" }}>
                  Station Tool Directory
                </span>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ background: "transparent", border: "none", color: "var(--muted)", fontSize: 16, cursor: "pointer", padding: "4px 8px" }}
                >
                  ✕
                </button>
              </div>
              <AdminNav barRoles={user.bar_roles} isMobile={true} />
            </div>
          </div>
        )}

        <main id="admin-content" style={{ ...styles.content, paddingBottom: isMobile ? "calc(72px + env(safe-area-inset-bottom, 0px))" : 16 }}>
          <ErrorBoundary fallbackTitle="Admin Studio Render Error">
            <Outlet context={{ user }} />
          </ErrorBoundary>
        </main>
        
        {!isMobile && (
          <footer style={styles.footer}>
            <button type="button" onClick={onLogout} style={styles.footerSignOut}>
              Sign out
            </button>
          </footer>
        )}

        {/* Persistent Bottom Thumb Navigation on Mobile */}
        {isMobile && (
          <AdminBottomNav
            barRoles={user.bar_roles}
            onOpenFullMenu={() => setMobileMenuOpen(true)}
          />
        )}
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  loading: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  desktopShell: {
    display: "flex",
    minHeight: "100vh",
  },
  mobileShell: {
    display: "block",
    minHeight: "100vh",
  },
  contentColumn: {
    flex: 1,
    minWidth: 0, // important for flex children that can overflow
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "12px 16px",
    borderBottom: "1px solid var(--border)",
    gap: 12,
  },
  mobileHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: 44,
    padding: "0 12px",
    background: "rgba(18, 14, 10, 0.92)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderBottom: "1px solid rgba(249, 175, 54, 0.18)",
    position: "sticky",
    top: 0,
    zIndex: 900,
  },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--gold)",
  },
  subEyebrow: {
    margin: "2px 0 0",
    fontSize: 10,
    fontWeight: 700,
    color: "var(--muted)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  headerRight: {
    display: "flex",
    gap: 6,
  },
  linkBtn: {
    background: "transparent",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    textDecoration: "none",
  },
  mobileLinkBtn: {
    background: "rgba(249, 175, 54, 0.1)",
    color: "var(--gold)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    borderRadius: 8,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
  },
  content: {
    padding: 16,
    maxWidth: "100%",
  },
  footer: {
    marginTop: 48,
    paddingTop: 24,
    paddingBottom: 24,
    borderTop: "1px solid var(--border)",
    textAlign: "center",
  },
  footerSignOut: {
    // WCAG 1.4.3 — drop opacity:0.5; muted color on bg already passes 8:1.
    // Sign out is intentionally low-emphasis but still readable.
    background: "transparent",
    color: "var(--muted)",
    border: 0,
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    padding: "8px 16px",
    letterSpacing: 0.4,
    textTransform: "uppercase",
    textDecoration: "underline",
    textUnderlineOffset: 3,
  },
};
